import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { registerUser } from "store/features/auth/authActions";
import { selectAuthLoading } from "store/features/auth/authSelectors";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import registerGirl from "assets/login/sign-up.svg";

export default function Register() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);

  const [errors, setErrors] = useState({});
  const [isFocused, setIsFocused] = useState({});
  const [formData, setFormData] = useState({
    tenant_name: "",
    subdomain: "",
    email: "",
    password: "",
    phone: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (formData.subdomain.length < 3) {
      setErrors({ subdomain: "Subdomain must be at least 3 characters" });
      return;
    }

    const result = await dispatch(registerUser(formData));

    if (result.success) {
      setTimeout(() => {
        navigate("/auth/sign-in");
      }, 2000);
    } else if (result.field === "subdomain") {
      setErrors({ subdomain: result.error });
    } else if (result.error) {
      showErrorToast(result.error);
    }
  };

  const getSubdomainStatus = () => {
    if (!formData.subdomain) return null;
    if (errors.subdomain) {
      return {
        icon: <WarningIcon sx={{ fontSize: 16, color: "#ef4444" }} />,
        text: errors.subdomain,
        color: "#ef4444",
      };
    }
    if (formData.subdomain.length >= 3) {
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#10b981" }} />,
        text: "Available",
        color: "#10b981",
      };
    }
    return null;
  };

  const subdomainStatus = getSubdomainStatus();

  const getFieldStatus = (fieldName) => {
    if (!formData[fieldName]) return null;
    if (errors[fieldName]) return "error";
    if (formData[fieldName].length > 0) return "success";
    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "500px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%" }}
      >
        <Paper
          elevation={24}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "24px",
            p: { xs: 3, lg: 4 },
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 251, 235, 0.95) 100%)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(245, 158, 11, 0.15)"
                : "rgba(245, 158, 11, 0.2)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
                : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            ml: { lg: "-5%" },
          }}
        >
          {/* Decorative glow effects */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              bottom: -50,
              left: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Small SVG inside the card - Top Right */}
          <Box
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              opacity: 0.9,
              ...(theme.breakpoints.up("lg") && {
                right: 24,
                top: 24,
              }),
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Box
                component="img"
                src={registerGirl}
                alt="Register illustration"
                sx={{
                  width: { xs: 80, lg: 96 },
                  height: { xs: 80, lg: 96 },
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                }}
              />
            </motion.div>
          </Box>

          {/* Logo and Title */}
          <Box sx={{ mb: 4, textAlign: "left" }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.875rem", lg: "2.25rem" },
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Create Account
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  color: theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                }}
              >
                Sign up to manage your office effortlessly.
              </Typography>
            </motion.div>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {/* Company Name */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <TextField
                  fullWidth
                  name="tenant_name"
                  label="Company Name"
                  value={formData.tenant_name}
                  onChange={handleChange}
                  onFocus={() => handleFocus("tenant_name")}
                  onBlur={() => handleBlur("tenant_name")}
                  required
                  placeholder="Enter your company name"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: isFocused.tenant_name
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon
                          sx={{
                            color: isFocused.tenant_name
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "14px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isFocused.tenant_name
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#334155"
                          : "#e5e7eb",
                        borderWidth: isFocused.tenant_name ? "2px" : "1px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f59e0b",
                      },
                      transition: "all 0.2s",
                      transform: isFocused.tenant_name
                        ? "scale(1.02)"
                        : "scale(1)",
                      backgroundColor: isFocused.tenant_name
                        ? theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(255, 255, 255, 0.9)"
                        : "transparent",
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      py: "14px",
                      fontSize: "0.95rem",
                    },
                  }}
                />
              </motion.div>

              {/* Subdomain */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Box>
                  <TextField
                    fullWidth
                    name="subdomain"
                    label="Subdomain"
                    value={formData.subdomain}
                    onChange={handleChange}
                    onFocus={() => handleFocus("subdomain")}
                    onBlur={() => handleBlur("subdomain")}
                    required
                    placeholder="Enter your subdomain (e.g. mycompany)"
                    variant="outlined"
                    error={!!errors.subdomain && !!formData.subdomain}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        color: isFocused.subdomain
                          ? "#f59e0b"
                          : errors.subdomain
                          ? "#ef4444"
                          : theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#6b7280",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon
                            sx={{
                              color: isFocused.subdomain
                                ? "#f59e0b"
                                : errors.subdomain
                                ? "#ef4444"
                                : theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                            }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            errors.subdomain && formData.subdomain
                              ? "#ef4444"
                              : isFocused.subdomain
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#334155"
                              : "#e5e7eb",
                          borderWidth: isFocused.subdomain ? "2px" : "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            errors.subdomain && formData.subdomain
                              ? "#ef4444"
                              : "#f59e0b",
                        },
                        transition: "all 0.2s",
                        transform: isFocused.subdomain
                          ? "scale(1.02)"
                          : "scale(1)",
                        backgroundColor: isFocused.subdomain
                          ? theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(255, 255, 255, 0.9)"
                          : "transparent",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        py: "14px",
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                  {subdomainStatus && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mt: 1 }}
                    >
                      {subdomainStatus.icon}
                      <Typography
                        variant="caption"
                        sx={{ color: subdomainStatus.color }}
                      >
                        {subdomainStatus.text}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </motion.div>

              {/* Email */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus("email")}
                  onBlur={() => handleBlur("email")}
                  required
                  placeholder="Enter your email"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: isFocused.email
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon
                          sx={{
                            color: isFocused.email
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "14px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isFocused.email
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#334155"
                          : "#e5e7eb",
                        borderWidth: isFocused.email ? "2px" : "1px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f59e0b",
                      },
                      transition: "all 0.2s",
                      transform: isFocused.email ? "scale(1.02)" : "scale(1)",
                      backgroundColor: isFocused.email
                        ? theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(255, 255, 255, 0.9)"
                        : "transparent",
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      py: "14px",
                      fontSize: "0.95rem",
                    },
                  }}
                />
              </motion.div>

              {/* Phone */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
              >
                <TextField
                  fullWidth
                  name="phone"
                  type="tel"
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => handleFocus("phone")}
                  onBlur={() => handleBlur("phone")}
                  required
                  placeholder="XXXXXXXXXX"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: isFocused.phone
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon
                          sx={{
                            color: isFocused.phone
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "14px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isFocused.phone
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#334155"
                          : "#e5e7eb",
                        borderWidth: isFocused.phone ? "2px" : "1px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f59e0b",
                      },
                      transition: "all 0.2s",
                      transform: isFocused.phone ? "scale(1.02)" : "scale(1)",
                      backgroundColor: isFocused.phone
                        ? theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(255, 255, 255, 0.9)"
                        : "transparent",
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      py: "14px",
                      fontSize: "0.95rem",
                    },
                  }}
                />
              </motion.div>
              {/* Password - Alternative with wrapper */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => handleFocus("password")}
                    onBlur={() => handleBlur("password")}
                    required
                    placeholder="Create a password"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        color: isFocused.password
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#6b7280",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon
                            sx={{
                              color: isFocused.password
                                ? "#f59e0b"
                                : theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                            }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isFocused.password
                            ? "#f59e0b"
                            : theme.palette.mode === "dark"
                            ? "#334155"
                            : "#e5e7eb",
                          borderWidth: isFocused.password ? "2px" : "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f59e0b",
                        },
                        transition: "all 0.2s",
                        transform: isFocused.password
                          ? "scale(1.02)"
                          : "scale(1)",
                        backgroundColor: isFocused.password
                          ? theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(255, 255, 255, 0.9)"
                          : "transparent",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        py: "14px",
                        fontSize: "0.95rem",
                        pr: "40px", // Make room for the eye button
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      color: showPassword
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      "&:hover": {
                        color: "primary.main",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 20 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Box>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
              >
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "14px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.4)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                    "&:active": {
                      transform: "translateY(1px)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "#ffffff", fontWeight: 500 }}
                      >
                        Registering...
                      </Typography>
                    </Stack>
                  ) : (
                    "Register"
                  )}
                </Button>
              </motion.div>

              {/* Login Link */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Box sx={{ pt: 1, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                    }}
                  >
                    Already have an account?{" "}
                    <Link
                      to="/auth/sign-in"
                      style={{
                        fontWeight: 600,
                        color: "primary.main",
                        textDecoration: "none",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = "#d97706";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = "#f59e0b";
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </motion.div>
            </Stack>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
}
